import { useCallback, useRef, useState } from 'react'

// Assets live in /public/inauguration (served at root by Vite).
const CURTAIN_IMG = '/inauguration/curtain.jpg'
const TUTARI_AUDIO = '/inauguration/tutari.mp3'

// How long the curtain halves take to slide apart (set inline on each half
// below). After this long the halves are fully off-screen and the overlay is
// removed from the DOM. Kept as a constant so the setTimeout stays in sync.
const CURTAIN_OPEN_MS = 8000

/**
 * Inauguration "curtain raiser" overlay.
 *
 * Ported from the notodrugs.live `/curtain` feature: two curtain halves
 * (left/right) cover the screen with a centered "लोकार्पण" button. On click,
 * the tutari (traditional horn) plays and the halves slide apart over 8s,
 * progressively revealing the app underneath, then this overlay unmounts.
 *
 * Rendered as a `fixed inset-0 z-[9999]` overlay so it covers the first page
 * the visitor lands on without interfering with routing. The parent gates
 * how often it shows (once per browser session via sessionStorage in App.tsx).
 *
 * Audio is created on the user's click (a valid play gesture) so browsers
 * allow it; `play()` failures are swallowed so the curtain still opens even
 * if audio is blocked.
 */
export default function InaugurationCurtain({ onDone }: { onDone?: () => void }) {
  const [opened, setOpened] = useState(false)
  const [hidden, setHidden] = useState(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  const handleInaugurate = useCallback(() => {
    if (opened) return
    if (!audioRef.current) {
      audioRef.current = new Audio(TUTARI_AUDIO)
      audioRef.current.volume = 1
    }
    audioRef.current.currentTime = 0
    audioRef.current.play().catch(() => {})
    setOpened(true)
    // Drop the overlay once the slide animation has finished.
    window.setTimeout(() => {
      setHidden(true)
      onDone?.()
    }, CURTAIN_OPEN_MS)
  }, [opened, onDone])

  if (hidden) return null

  return (
    <div className="fixed inset-0 z-[9999] overflow-hidden">
      {/* Black backdrop, fades out on click so the app shows through the
          widening gap as the curtain halves slide apart. */}
      <div
        className={`absolute inset-0 bg-black transition-opacity ${
          opened ? 'opacity-0' : 'opacity-100'
        }`}
        style={{ transitionDuration: '1500ms', transitionTimingFunction: 'linear' }}
      />
      {/* The two curtain halves. Same image, mirrored via backgroundPosition.
          Duration/timing are set inline (not as arbitrary Tailwind classes)
          to avoid ambiguous-class warnings. */}
      <div
        className={`absolute inset-0 z-40 flex ${
          opened ? 'pointer-events-none' : 'pointer-events-auto'
        }`}
      >
        <div
          className={`w-1/2 h-full transition-transform ${
            opened ? '-translate-x-full' : 'translate-x-0'
          }`}
          style={{
            transitionDuration: '8000ms',
            transitionTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)',
            backgroundImage: `url(${CURTAIN_IMG})`,
            backgroundSize: 'cover',
            backgroundPosition: 'left center',
            backgroundRepeat: 'no-repeat',
          }}
        />
        <div
          className={`w-1/2 h-full transition-transform ${
            opened ? 'translate-x-full' : 'translate-x-0'
          }`}
          style={{
            transitionDuration: '8000ms',
            transitionTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)',
            backgroundImage: `url(${CURTAIN_IMG})`,
            backgroundSize: 'cover',
            backgroundPosition: 'right center',
            backgroundRepeat: 'no-repeat',
          }}
        />
        {!opened && (
          <div className="absolute inset-0 flex items-center justify-center z-50">
            <button
              onClick={handleInaugurate}
              className="bg-yellow-400 text-black px-8 py-3 rounded-md font-semibold shadow-lg hover:scale-105 transition"
            >
              लोकार्पण
            </button>
          </div>
        )}
      </div>
    </div>
  )
}